import { useState, useRef, useCallback, useEffect } from 'react';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { getNavLabel } from '../../constants/navLabels';
import { PortalsSideNavIcon } from './PortalsSideNavIcon';
import { useIsPillBorderRadius } from '../../hooks/useIsPillBorderRadius';
import { getIsThemePreview } from '../../utils/metadata';
import styled from 'foundations-theming-specialty/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledView = styled(View)(({
  theme,
  $isPill
}) => ({
  borderRadius: theme.borderRadius[$isPill ? '0' : '100'],
  transition: 'background-color 150ms',
  '&:hover, &[data-vr-state="hover"]': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));
export function PortalsSideNavItem({
  item,
  history
}) {
  var _item$href;
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef(undefined);
  const isPill = useIsPillBorderRadius();
  const hasChildren = item.children.length > 0;
  const handleClientNav = useCallback(navItem => e => {
    if (history && navItem.isCurrent && navItem.path) {
      e.preventDefault();
      history.push(navItem.path);
    } else if (getIsThemePreview()) {
      e.preventDefault();
    }
  }, [history]);
  useEffect(() => () => clearTimeout(closeTimeout.current), []);
  const show = useCallback(() => {
    clearTimeout(closeTimeout.current);
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);
  const popoverBody = hasChildren ? /*#__PURE__*/_jsx(View, {
    as: "span",
    typography: "body-100",
    color: "text-core-default",
    children: /*#__PURE__*/_jsx(View, {
      onMouseEnter: show,
      onMouseLeave: hide,
      padding: "200",
      children: item.children.map(child => {
        var _child$href;
        return /*#__PURE__*/_jsx(StyledView, {
          $isPill: isPill,
          as: "div",
          "data-test-id": `portals-side-nav-popover-item-${child.id}`,
          children: /*#__PURE__*/_jsx(Link, {
            href: (_child$href = child.href) !== null && _child$href !== void 0 && _child$href.startsWith('/') ? child.href : `//${child.href}`,
            external: false,
            display: "block",
            paddingBlock: "100",
            paddingInline: "200",
            use: "unstyled",
            onClick: handleClientNav(child),
            children: /*#__PURE__*/_jsx(View, {
              as: "span",
              typography: "body-100",
              color: "text-core-default",
              children: getNavLabel(child.id)
            })
          })
        }, child.id);
      })
    })
  }) : /*#__PURE__*/_jsx(View, {
    onMouseEnter: show,
    onMouseLeave: hide,
    paddingBlock: "100",
    paddingInline: "200",
    children: /*#__PURE__*/_jsx(View, {
      as: "span",
      typography: "body-100",
      color: "text-core-default",
      children: getNavLabel(item.id)
    })
  });
  const iconBorderRadius = isPill ? '0' : '300';
  const triggerBackground = item.isActive || open ? 'fill-accent-neutral-subtle' : undefined;
  const trigger = hasChildren ? /*#__PURE__*/_jsx(Flex, {
    borderRadius: iconBorderRadius,
    alignItems: "center",
    justifyContent: "center",
    inlineSize: "40px",
    blockSize: "40px",
    backgroundColor: triggerBackground,
    children: /*#__PURE__*/_jsx(PortalsSideNavIcon, {
      name: item.id
    })
  }) : /*#__PURE__*/_jsx(Flex, {
    borderRadius: iconBorderRadius,
    inlineSize: "40px",
    blockSize: "40px",
    backgroundColor: triggerBackground,
    children: /*#__PURE__*/_jsx(Link, {
      href: (_item$href = item.href) !== null && _item$href !== void 0 && _item$href.startsWith('/') ? item.href : `//${item.href}`,
      use: "unstyled",
      display: "flex",
      inlineSize: "100%",
      blockSize: "100%",
      alignItems: "center",
      justifyContent: "center",
      onClick: handleClientNav(item),
      children: /*#__PURE__*/_jsx(PortalsSideNavIcon, {
        name: item.id
      })
    })
  });
  return /*#__PURE__*/_jsxs(View, {
    "data-test-id": `portals-side-nav-item-${item.id}`,
    position: "relative",
    onMouseEnter: show,
    onMouseLeave: hide,
    children: [/*#__PURE__*/_jsx(Popover, {
      open: open,
      onOpenChange: ({
        target: {
          value
        }
      }) => setOpen(value),
      content: popoverBody,
      placement: "right bottom",
      arrowSize: "none",
      closeOnOutsideClick: false,
      children: trigger
    }), item.isActive && /*#__PURE__*/_jsx(Flex, {
      position: "absolute",
      insetInlineEnd: "0",
      insetBlockStart: "0",
      insetBlockEnd: "0",
      alignItems: "center",
      children: /*#__PURE__*/_jsx(Flex, {
        inlineSize: "2px",
        blockSize: "22px",
        borderRadius: "full",
        backgroundColor: "fill-accent-neutral-default"
      })
    })]
  });
}